import React, { useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CommonButton from "../../../components/commonButton";
import InputField from "../../../components/form/InputField";
import { FormLabel } from "../../../components/form/utils";
import { useToast } from "../../../context/TostContext";
import { usePermission } from "../../../hooks/usePermission";
import { fetchGisTrialConfig, updateGisTrialConfig } from "../../../redux/slice/gisTrialSlice";

const MODULE = "subscription_plans";

const validationSchema = Yup.object({
    trial_days: Yup.number()
        .required("Trial days required")
        .min(1, "Min 1 day")
        .max(365, "Max 365 days"),
    available_from: Yup.string().nullable(),
    available_until: Yup.string()
        .nullable()
        .test(
            "after-from",
            "Must be after the start date",
            function (value) {
                const { available_from } = this.parent;
                if (!value || !available_from) return true;
                return new Date(value) > new Date(available_from);
            }
        ),
});

/** Backend stores ISO timestamps; the date input wants YYYY-MM-DD. */
const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

const SettingsCard = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { hasPermission } = usePermission();
    const { config, isSaving } = useSelector((state) => state.gisTrial || {});

    const canEdit = hasPermission(MODULE, "update");

    useEffect(() => {
        dispatch(fetchGisTrialConfig());
    }, [dispatch]);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const result = await dispatch(
                updateGisTrialConfig({
                    enabled: values.enabled,
                    trial_days: Number(values.trial_days),
                    one_per_identity: values.one_per_identity,
                    requires_verified_mobile: values.requires_verified_mobile,
                    available_from: values.available_from || null,
                    available_until: values.available_until || null,
                })
            ).unwrap();

            if (result?.success) {
                toast.success(result.message || "GIS trial settings saved");
            }
        } catch (err) {
            toast.error(err?.message || "Failed to save settings");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-2 common-shadow p-4">
            <div className="d-flex align-items-center justify-content-between mb-1">
                <h5 className="fw-semibold mb-0">Free trial settings</h5>
                <span
                    className={`badge rounded-pill ${config?.enabled ? "bg-success" : "bg-secondary"}`}
                >
                    {config?.enabled ? "Live" : "Off"}
                </span>
            </div>
            <p className="text-muted small mb-4">
                While this is off, the app refuses every request to start a GIS trial.
                Existing trials keep running until they expire.
            </p>

            <Formik
                enableReinitialize
                initialValues={{
                    enabled: config?.enabled ?? false,
                    trial_days: config?.trial_days ?? 14,
                    one_per_identity: config?.one_per_identity ?? true,
                    requires_verified_mobile: config?.requires_verified_mobile ?? true,
                    available_from: toDateInput(config?.available_from),
                    available_until: toDateInput(config?.available_until),
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col md={6}>
                                <FormLabel labelName="Trial enabled" />
                                <Form.Check
                                    type="switch"
                                    id="gis-trial-enabled"
                                    label={values.enabled ? "Users can start a trial" : "Trial unavailable"}
                                    checked={values.enabled}
                                    disabled={!canEdit}
                                    onChange={(e) => setFieldValue("enabled", e.target.checked)}
                                />
                            </Col>

                            <Col md={6}>
                                <InputField
                                    label={{ name: "Trial length (days)" }}
                                    input={{
                                        id: "trial_days",
                                        name: "trial_days",
                                        type: "number",
                                        min: 1,
                                        max: 365,
                                        disabled: !canEdit,
                                        value: values.trial_days,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        isInvalid: touched.trial_days && !!errors.trial_days,
                                        message: errors.trial_days,
                                    }}
                                />
                            </Col>

                            <Col md={6}>
                                <InputField
                                    label={{ name: "Available from" }}
                                    input={{
                                        id: "available_from",
                                        name: "available_from",
                                        type: "date",
                                        disabled: !canEdit,
                                        value: values.available_from,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        isInvalid: touched.available_from && !!errors.available_from,
                                        message: errors.available_from,
                                    }}
                                />
                                <small className="text-muted">Leave empty for no start date.</small>
                            </Col>

                            <Col md={6}>
                                <InputField
                                    label={{ name: "Available until" }}
                                    input={{
                                        id: "available_until",
                                        name: "available_until",
                                        type: "date",
                                        disabled: !canEdit,
                                        value: values.available_until,
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        isInvalid: touched.available_until && !!errors.available_until,
                                        message: errors.available_until,
                                    }}
                                />
                                <small className="text-muted">Leave empty to run indefinitely.</small>
                            </Col>

                            <Col md={6}>
                                <FormLabel labelName="One trial per person" />
                                <Form.Check
                                    type="switch"
                                    id="gis-one-per-identity"
                                    label="Match on verified mobile number"
                                    checked={values.one_per_identity}
                                    disabled={!canEdit}
                                    onChange={(e) => setFieldValue("one_per_identity", e.target.checked)}
                                />
                                <small className="text-muted">
                                    Stops someone getting a second trial by registering again.
                                </small>
                            </Col>

                            <Col md={6}>
                                <FormLabel labelName="Require verified mobile" />
                                <Form.Check
                                    type="switch"
                                    id="gis-requires-verified-mobile"
                                    label="Only verified users can start a trial"
                                    checked={values.requires_verified_mobile}
                                    disabled={!canEdit}
                                    onChange={(e) => setFieldValue("requires_verified_mobile", e.target.checked)}
                                />
                            </Col>
                        </Row>

                        {canEdit && (
                            <div className="d-flex justify-content-end mt-4">
                                <CommonButton
                                    type="submit"
                                    variant="primary"
                                    title={isSaving || isSubmitting ? "Saving..." : "Save settings"}
                                    disabled={isSaving || isSubmitting}
                                />
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default SettingsCard;
